import { minorUnitsToAmount } from "@redotech/money/iso4217/currencies";
import { DEFAULT_CURRENCY } from "@redotech/money/util";
import * as zod from "zod";
import { InventoryPolicy } from "../draft-return/draft-return-items";
import { ImageId } from "../ids";
import { deriveImageUrl } from "../image-url";
import { ReturnTypeEnum } from "../return";
import { isVariantInStock } from "../returns/products/product-variant-utils";
import { Settings } from "../team";

const VariantOptionSchema_ = zod.object({
  name: zod.string(),
  value: zod.string(),
  productAsVariant: zod.boolean().nullish(),
});
export const VariantOptionSchema: zod.ZodType<VariantOption> =
  VariantOptionSchema_;
export interface VariantOption extends zod.infer<typeof VariantOptionSchema_> {}

const PriceSchema_ = zod.object({
  amount: zod.string(),
  currency: zod.string(),
});
export const PriceSchema: zod.ZodType<Price> = PriceSchema_;
export interface Price extends zod.infer<typeof PriceSchema_> {}

const VariantImageSchema_ = zod.object({
  url: zod.string(),
  altText: zod.string().optional(),
});
export const VariantImageSchema: zod.ZodType<VariantImage> =
  VariantImageSchema_;
export interface VariantImage extends zod.infer<typeof VariantImageSchema_> {}

export const VariantMetafieldsSchema = zod
  .record(zod.string(), zod.string().nullish())
  .optional();
export type VariantMetafields = zod.infer<typeof VariantMetafieldsSchema>;

const VariantDataSchema_ = zod.object({
  variantId: zod.string(),
  productId: zod.string().nullish(),
  productTitle: zod.string().nullish(),
  variantTitle: zod.string(),
  price: PriceSchema,
  compareAtPrice: PriceSchema.optional(),
  image: VariantImageSchema,
  options: zod.array(VariantOptionSchema),
  onlineInventoryAvailable: zod.number(),
  // POS-only: inventory available at the specific POS store location.
  // When present, onlineInventoryAvailable represents total shippable inventory.
  posLocationInventoryAvailable: zod.number().optional(),
  // this can be true even if onlineInventoryAvailable is 0 depending on the merchant's settings
  // (e.g. if continue when out of stock is enabled)
  isInStock: zod.boolean(),
  backorder: zod.string().nullish(),
  sku: zod.string().nullish(),
  barcode: zod.string().nullish(),
  metafields: VariantMetafieldsSchema,
});
export const VariantDataSchema: zod.ZodType<VariantData> = VariantDataSchema_;
export interface VariantData extends zod.infer<typeof VariantDataSchema_> {}

const ProductVariantDataSchema_ = VariantDataSchema_.extend({
  productId: zod.string(),
  productTitle: zod.string(),
});
export const ProductVariantDataSchema: zod.ZodType<ProductVariantData> =
  ProductVariantDataSchema_;
export interface ProductVariantData
  extends zod.infer<typeof ProductVariantDataSchema_> {}

const ProductVariantDataWithQuantitySchema_ = ProductVariantDataSchema_.extend({
  quantity: zod.number(),
});
export const ProductVariantDataWithQuantitySchema: zod.ZodType<ProductVariantDataWithQuantity> =
  ProductVariantDataWithQuantitySchema_;
export interface ProductVariantDataWithQuantity
  extends zod.infer<typeof ProductVariantDataWithQuantitySchema_> {}

const ShopOnSiteProductDataSchema_ = zod.object({
  onlineStoreUrl: zod.string().optional(),
  handle: zod.string().optional(),
});
export const ShopOnSiteProductDataSchema: zod.ZodType<ShopOnSiteProductData> =
  ShopOnSiteProductDataSchema_;
export interface ShopOnSiteProductData
  extends zod.infer<typeof ShopOnSiteProductDataSchema_> {}

const ProductOptionSchema_ = zod.object({
  name: zod.string(),
  values: zod.array(zod.string()),
});
export const ProductOptionSchema: zod.ZodType<ProductOption> =
  ProductOptionSchema_;
export interface ProductOption extends zod.infer<typeof ProductOptionSchema_> {}

const ProductImageSchema_ = zod.object({
  url: zod.string(),
  alt: zod.string().nullish(),
});
export const ProductImageSchema: zod.ZodType<ProductImage> =
  ProductImageSchema_;
export interface ProductImage extends zod.infer<typeof ProductImageSchema_> {}

const ProductDataSchema_ = zod.object({
  productId: zod.string(),
  productTitle: zod.string(),
  options: zod.array(ProductOptionSchema),
  images: zod.array(ProductImageSchema).nullish(),
  variants: zod.array(VariantDataSchema),
  shopOnSite: ShopOnSiteProductDataSchema.optional(),
});
export const ProductDataSchema: zod.ZodType<ProductData> = ProductDataSchema_;
export interface ProductData extends zod.infer<typeof ProductDataSchema_> {}

interface CrdbImage {
  id: ImageId;
  name: string;
  mime_type: string | null;
  thumbhash: string | null;
}

// In CRDB, the `product` table stores individual variants (SKU-level items),
// while `product_family` is the parent product. So a CrdbExchangeProduct
// represents a single variant with its own price, inventory, and options.
export interface CrdbExchangeProduct {
  id: string;
  title: string;
  sku: string | null;
  price: number | null;
  currency: string | null;
  image: CrdbImage | null;
  fallback_image: string | null;
  image_alt_text: string | null;
  available: number;
  inventory_policy: "CONTINUE" | "DENY";
  inventory_tracked: boolean;
  shopify_variant_id: string | null;
  options: { name: string; value: string }[];
}

export interface CrdbExchangeFamily {
  title: string;
  description: string;
  vendor: string | null;
  tags: string[] | null;
  images: {
    altText: string | null;
    fallback_image: string | null;
    image: CrdbImage | null;
  }[];
  options: { name: string; values: { value: string }[] }[];
  products: CrdbExchangeProduct[];
}

/**
 * Converts a CRDB product family (from getProductFamiliesForExchange) to ProductData.
 */
export function convertCrdbProductFamilyToProductData(
  family: CrdbExchangeFamily,
  shopifyProductGid: string,
  shopCurrency = "USD",
  inventorySettings: Settings["inventory"],
  returnType: ReturnTypeEnum,
): ProductData {
  const familyImages: ProductImage[] = family.images.map((img) => ({
    url: img.fallback_image || deriveImageUrl(img.image, null),
    alt: img.altText,
  }));

  const defaultImage: VariantImage = familyImages[0]
    ? { url: familyImages[0].url, altText: familyImages[0].alt ?? undefined }
    : { url: "", altText: undefined };

  // Track which option values are actually present on at least one variant.
  // We still want to drop option values with no matching variant, but the
  // *display order* must come from `family.options` (which is sorted by the
  // Shopify-authored option position), not from the iteration order of
  // `family.products` — that order is unspecified by the SQL query and
  // produces randomized size pickers (e.g. XL, 3XL, 2XL, L, S, M, XS).
  const presentValuesByOption = new Map<string, Set<string>>();

  // family.products are CRDB `product` rows, which represent individual variants
  const variants: VariantData[] = family.products.map((product) => {
    const variantOptions: VariantOption[] = product.options.map((opt) => {
      let presentValues = presentValuesByOption.get(opt.name);
      if (!presentValues) {
        presentValues = new Set();
        presentValuesByOption.set(opt.name, presentValues);
      }
      presentValues.add(opt.value);
      return { name: opt.name, value: opt.value };
    });

    const variantTitle =
      variantOptions.map((opt) => opt.value).join(" / ") || "Default";

    const currency = product.currency || shopCurrency || DEFAULT_CURRENCY;
    const priceAmount =
      product.price !== null
        ? minorUnitsToAmount(product.price, currency).toString()
        : "0";

    const variantImage: VariantImage = product.fallback_image
      ? {
          url: product.fallback_image,
          altText: product.image_alt_text ?? undefined,
        }
      : product.image
        ? {
            url: deriveImageUrl(product.image, null),
            altText: product.image_alt_text ?? undefined,
          }
        : defaultImage;

    return {
      variantId: product.shopify_variant_id ?? product.id,
      productId: shopifyProductGid,
      productTitle: family.title,
      variantTitle,
      price: { amount: priceAmount, currency },
      image: variantImage,
      options: variantOptions,
      onlineInventoryAvailable: product.available,
      isInStock: isVariantInStock({
        inventorySettings,
        returnType,
        inventoryQuantity: product.available,
        inventoryTracked: product.inventory_tracked,
        inventoryPolicy:
          product.inventory_policy === "CONTINUE"
            ? InventoryPolicy.CONTINUE
            : InventoryPolicy.DENY,
      }),
      sku: product.sku,
      metafields: undefined,
    };
  });

  const options: ProductOption[] = [];
  for (const familyOption of family.options) {
    const presentValues = presentValuesByOption.get(familyOption.name);
    if (!presentValues || presentValues.size === 0) {
      continue;
    }
    const orderedValues = familyOption.values
      .map((v) => v.value)
      .filter((value) => presentValues.has(value));
    if (orderedValues.length === 0) {
      continue;
    }
    options.push({ name: familyOption.name, values: orderedValues });
  }

  return {
    productId: shopifyProductGid,
    productTitle: family.title,
    options,
    images: familyImages.length > 0 ? familyImages : null,
    variants,
  };
}
